<script>
import moment from 'moment';
import { fromApiDatetime, DISPLAY_DATETIME } from '@/util/apiDatetime';
import { secondsToHms } from '@/util/date';
import { adjustmentTypeLabel } from '@/util/adjustment';
import { formatNumOrBlank } from '@/util/number';

export default {
  name: 'RobDetail',
  props: {
    selectedRobDetail: Object,
    detailLoading: Boolean,
    detailError: String,
    adjustmentData: Array,
    adjustmentLoading: Boolean,
    adjustmentError: String,
    confirmingDeleteAdjustmentId: [Number, String],
    summaryLoading: Boolean, // Prop for summary-specific loading
  },
  data() {
    return {
      detailViewType: 'summary',
      summaryInterval: 'daily',
      expandedDebugRows: {},
    };
  },
  computed: {
    // Kolom Actions cuma ada kalau boleh edit atau hapus
    canManageRobAdjustment() {
      return this.canUpdate('master_rob_adjustment') || this.canDelete('master_rob_adjustment');
    },
    // Extracts and flattens all logs from the nested structure to display in a single table
    flattenedLogs() {
      if (!this.selectedRobDetail?.summary?.details) return [];
      const logs = [];
      this.selectedRobDetail.summary.details.forEach((day) => {
        Object.keys(day.logs).forEach((timestamp) => {
          const logEntries = day.logs[timestamp];
          Object.keys(logEntries).forEach((entryType) => {
            logEntries[entryType].forEach((entry) => {
              logs.push({
                timestamp: parseInt(timestamp, 10),
                ...entry,
                entryType,
              });
            });
          });
        });
      });
      return logs.sort((a, b) => a.timestamp - b.timestamp);
    },
    // Easy access to the rob summary data
    robSummary() {
      return this.selectedRobDetail?.summary?.rob || null;
    },
  },
  watch: {
    summaryInterval(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit('summary-interval-change', newValue);
      }
    },

  },
  methods: {
    formatNumber(value) {
      return formatNumOrBlank(value);
    },
    toggleDetailView(type) {
      this.detailViewType = type;
    },
    // Formatting methods
    formatDateToLocal(dateString) {
      if (!dateString) return '';
      return fromApiDatetime(dateString).format(DISPLAY_DATETIME);
    },
    formatUnixTimestampToLocal(unixTimestamp) {
      if (!unixTimestamp) return '';
      return moment.unix(unixTimestamp).format(DISPLAY_DATETIME);
    },
    formatAdjustmentType(type) {
      return adjustmentTypeLabel(type);
    },
    formatEntryType(type) {
      switch (type) {
        case 'fuelcons': return 'Fuel Consumption';
        case 'bunker': return 'Bunker';
        case 'adjustments': return 'Adjustment';
        default: return type;
      }
    },
    entryTypeBadgeClass(type) {
      switch (type) {
        case 'fuelcons': return 'badge-warning text-dark';
        case 'bunker': return 'badge-success';
        case 'adjustments': return 'badge-primary';
        default: return 'badge-secondary';
      }
    },
    toggleDebugRow(index) {
      this.$set(this.expandedDebugRows, index, !this.expandedDebugRows[index]);
    },
    isDebugRowExpanded(index) {
      return !!this.expandedDebugRows[index];
    },
    hasCoverageIssues(debug) {
      const inventories = Object.values(debug?.flowmeterInventory || {});
      if (inventories.some(
        (inv) => inv.coverage.missingStart || inv.coverage.missingEnd || inv.backward,
      )) return true;

      // out_exceeds_in gak ninggalin jejak di level device, cuma di sini.
      return ['flowmeter', 'ae'].some((type) => debug?.rollup?.[type]?.coverage?.warnings?.length);
    },
    // Blok rollup null kalau kapal gak punya device tipe itu.
    rollupBadges(debug) {
      return ['flowmeter', 'ae'].flatMap((type) => {
        const block = debug?.rollup?.[type];
        if (!block) return [];

        const coverage = block.coverage || {};
        const warnings = coverage.warnings || [];
        const gapCount = coverage.gaps?.length || 0;
        const parts = [block.source];

        // Floor, bukan round: ratio 0.9998 dibulatin jadi "100%" padahal masih bolong.
        if (!coverage.complete) parts.push(`${Math.floor((coverage.ratio || 0) * 1000) / 10}%`);
        if (gapCount) parts.push(`${gapCount} gap${gapCount > 1 ? 's' : ''}${coverage.gapsTruncated ? '+' : ''}`);
        parts.push(...warnings);

        return [{
          type: type.toUpperCase(),
          label: parts.join(' · '),
          warn: !coverage.complete || warnings.length > 0,
        }];
      });
    },
    formatGapDuration(seconds) {
      if (!seconds) return '0s';
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      if (h > 0) return `${h}h ${m}m`;
      if (m > 0) return `${m}m`;
      return `${seconds}s`;
    },
    formatSecondsToHms(seconds) {
      return secondsToHms(seconds);
    },
    formatUtcDateToLocal(dateString) {
      if (!dateString) return '';
      const date = fromApiDatetime(dateString).toDate();
      const p = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())} ${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`;
    },
  },
};
</script>

<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <b-button variant="outline-secondary" size="sm" @click="$emit('back')" class="mr-2">
          <i class="ri-arrow-left-line"></i> Back
        </b-button>
        <h5 class="d-inline-block mb-0">ROB Profile Detail</h5>
      </div>
    </div>
    <div v-if="detailLoading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
      <p class="mt-3 text-muted">Loading ROB detail...</p>
    </div>
    <div v-else-if="detailError" class="alert alert-danger d-flex align-items-center">
      <i class="ri-error-warning-line mr-2" style="font-size: 1.2rem;"></i>
      {{ detailError }}
    </div>
    <div v-else-if="selectedRobDetail">
      <!-- ROB Profile Info Card -->
      <b-card class="mb-4">
        <template #header>
          <h5 class="mb-0">ROB Profile</h5>
        </template>
        <b-row>
          <b-col md="4" class="mb-3">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <i class="ri-time-line" style="font-size: 2.2rem; color: #50b5ff;"></i>
              </div>
              <div>
                <div class="text-muted small">Start Time</div>
                <strong>{{ formatDateToLocal(selectedRobDetail.master.mrobStartTime) }}</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <i class="ri-time-fill" style="font-size: 2.2rem; color: #50b5ff;"></i>
              </div>
              <div>
                <div class="text-muted small">End Time</div>
                <strong>{{ formatDateToLocal(selectedRobDetail.master.mrobEndTime) }}</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <i class="ri-gas-station-line" style="font-size: 2.2rem; color: #50b5ff;"></i>
              </div>
              <div>
                <div class="text-muted small">Initial Value</div>
                <strong>{{ formatNumber(selectedRobDetail.master.mrobInitialValue) }}</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <i class="ri-user-line" style="font-size: 2.2rem; color: #50b5ff;"></i>
              </div>
              <div>
                <div class="text-muted small">Created By</div>
                <strong>{{ selectedRobDetail.master.mrobCreatedUserId || '-' }}</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <i class="ri-calendar-event-line" style="font-size: 2.2rem; color: #50b5ff;"></i>
              </div>
              <div>
                <div class="text-muted small">Created At</div>
                <strong>{{ formatDateToLocal(selectedRobDetail.master.mrobCreatedTime) }}</strong>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- New Summary Cards -->
      <h5 class="mt-4" v-if="canRead('summary_rob')">Summary</h5>
      <div v-if="summaryLoading" class="text-center py-3">
        <b-spinner small variant="primary"></b-spinner>
        <span class="ml-2 text-muted">Refreshing summary...</span>
      </div>
      <div v-else-if="robSummary && canRead('summary_rob')">
        <b-row>
          <!-- Start Balance Card -->
          <b-col lg="2" md="4" sm="6" class="mb-4">
            <b-card class="summary-card h-100" no-body>
              <div class="card-header d-flex align-items-center" style="background-color: #e9f5ff; color: #007bff;">
                <i class="ri-inbox-unarchive-line mr-2" style="font-size: 1.2rem;"></i>
                <span class="font-weight-bold">Start Balance</span>
              </div>
              <b-card-body class="d-flex flex-column justify-content-center text-center">
                <h4 class="mb-1 font-weight-bold">{{ formatNumber(robSummary.balances.start) }}</h4>
                <small class="text-muted">liters</small>
              </b-card-body>
            </b-card>
          </b-col>
          <!-- Last Balance Card -->
          <b-col lg="2" md="4" sm="6" class="mb-4">
            <b-card class="summary-card h-100" no-body>
              <div class="card-header d-flex align-items-center" style="background-color: #e9f5ff; color: #007bff;">
                <i class="ri-inbox-archive-line mr-2" style="font-size: 1.2rem;"></i>
                <span class="font-weight-bold">Last Balance</span>
              </div>
              <b-card-body class="d-flex flex-column justify-content-center text-center">
                <h4 class="mb-1 font-weight-bold">{{ formatNumber(robSummary.balances.last) }}</h4>
                <small class="text-muted">liters</small>
              </b-card-body>
            </b-card>
          </b-col>
          <!-- Consumption Card -->
          <b-col lg="2" md="4" sm="6" class="mb-4">
            <b-card class="summary-card h-100" no-body>
              <div class="card-header d-flex align-items-center" style="background-color: #fff4e6; color: #fd7e14;">
                <i class="ri-fire-line mr-2" style="font-size: 1.2rem;"></i>
                <span class="font-weight-bold">Consumption</span>
              </div>
              <b-card-body class="d-flex flex-column justify-content-center text-center">
                <div class="text-muted small">Total Volume</div>
                <h4 class="mb-1 font-weight-bold">{{ formatNumber(robSummary.consumption.totalVolume) }}</h4>
                <small class="text-muted">liters</small>
              </b-card-body>
            </b-card>
          </b-col>
          <!-- Total Adjustment Vol Card -->
          <b-col lg="2" md="4" sm="6" class="mb-4">
            <b-card class="summary-card h-100" no-body>
              <div class="card-header d-flex align-items-center" style="background-color: #f3e9ff; color: #6f42c1;">
                <i class="ri-tools-line mr-2" style="font-size: 1.2rem;"></i>
                <span class="font-weight-bold">Total Adj. Vol</span>
              </div>
              <b-card-body class="d-flex flex-column justify-content-center text-center">
                <h4 class="mb-1 font-weight-bold">{{ formatNumber(robSummary.adjustments.totalVolume) }}</h4>
                <small class="text-muted">liters</small>
              </b-card-body>
            </b-card>
          </b-col>
          <!-- Last Adjustment Vol Card -->
          <b-col lg="2" md="4" sm="6" class="mb-4">
            <b-card class="summary-card h-100" no-body>
              <div class="card-header d-flex align-items-center" style="background-color: #f3e9ff; color: #6f42c1;">
                <i class="ri-tools-line mr-2" style="font-size: 1.2rem;"></i>
                <span class="font-weight-bold">Last Adj. Vol</span>
              </div>
              <b-card-body class="d-flex flex-column justify-content-center text-center">
                <h4 class="mb-1 font-weight-bold">{{ formatNumber(robSummary.adjustments.lastVolume) }}</h4>
                <small class="text-muted">liters</small>
              </b-card-body>
            </b-card>
          </b-col>
          <!-- Bunker Card -->
          <b-col lg="2" md="4" sm="6" class="mb-4">
            <b-card class="summary-card h-100" no-body>
              <div class="card-header d-flex align-items-center" style="background-color: #e4f8f0; color: #28a745;">
                <i class="ri-gas-station-fill mr-2" style="font-size: 1.2rem;"></i>
                <span class="font-weight-bold">Bunker (IN)</span>
              </div>
              <b-card-body class="d-flex flex-column justify-content-center text-center">
                <div class="text-muted small">Total Volume</div>
                <h4 class="mb-1 font-weight-bold">{{ robSummary.bunker && robSummary.bunker.IN ? formatNumber(robSummary.bunker.IN.totalVolume) : '-' }}</h4>
                <small class="text-muted">liters</small>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </div>
      <b-alert v-else-if="!summaryLoading && canRead('summary_rob')" show variant="warning">
        Summary data is not available for this ROB profile.
      </b-alert>

      <!-- View Toggler (Summary/Adjustment) -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <b-nav pills>
          <b-nav-item v-if="canRead('summary_rob')" :active="detailViewType === 'summary'"
            @click="toggleDetailView('summary')">Summary</b-nav-item>
          <b-nav-item v-if="canRead('master_rob_adjustment')" :active="detailViewType === 'adjustment'"
            @click="toggleDetailView('adjustment')">
            Adjustment
            <b-badge v-if="robSummary && robSummary.adjustments.count > 0" variant="success" class="ml-2">
              {{ robSummary.adjustments.count }} Adjusted
            </b-badge>
          </b-nav-item>
        </b-nav>
        <b-button v-if="detailViewType === 'adjustment' && canCreate('master_rob_adjustment')" variant="primary"
          @click="$emit('add-adjustment')">
          <i class="ri-add-line mr-1"></i> Add Adjustment
        </b-button>
      </div>

      <!-- Summary Detail Log -->
      <div v-if="detailViewType === 'summary' && canRead('summary_rob')">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Detail Log</h5>
          <div class="d-flex align-items-end">
            <b-form-group label="Interval" label-for="summary-interval-radios" class="mb-0" label-class="font-weight-bold">
              <b-form-radio-group id="summary-interval-radios" v-model="summaryInterval"
                :options="[ { text: 'Daily', value: 'daily' }, { text: 'Weekly', value: 'weekly' }, { text: 'Monthly', value: 'monthly' } ]"
                buttons button-variant="outline-primary" name="summary-interval-radios">
              </b-form-radio-group>
            </b-form-group>
          </div>
        </div>

        <!-- Log Table -->
        <div v-if="summaryLoading" class="text-center py-5">
          <b-spinner label="Loading..."></b-spinner>
          <p class="mt-2 text-muted">Loading log...</p>
        </div>
        <template v-else>
        <!-- Desktop: tabel log -->
        <div class="log-desktop-table">
          <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th style="width: 135px">Time</th>
                <th style="width: 135px">Type</th>
                <th>Note</th>
                <th style="width: 135px">Addition (L)</th>
                <th style="width: 135px">Reduction (L)</th>
                <th style="width: 135px">ROB Balance (L)</th>
                <th style="width: 50px">Cov.</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(log, index) in flattenedLogs">
                <tr :key="'row-' + index"
                  :class="{
                    'log-row-stripe': index % 2 !== 0,
                    'dfp-clickable-row': log.entryType === 'fuelcons' && log.debug,
                    'dfp-row-active': log.entryType === 'fuelcons' && log.debug && isDebugRowExpanded(index)
                  }"
                  @click="log.entryType === 'fuelcons' && log.debug ? toggleDebugRow(index) : null">
                  <td>{{ formatUnixTimestampToLocal(log.timestamp) }}</td>
                  <td>
                    <span class="badge" :class="entryTypeBadgeClass(log.entryType)">
                      {{ formatEntryType(log.entryType) }}
                    </span>
                  </td>
                  <td>
                    <a href="#" v-if="log.note" @click.prevent="$emit('show-note', log.note)">
                      {{ log.note.length > 50 ? log.note.substring(0, 50) + '...' : log.note }}
                    </a>
                  </td>
                  <td>
                    <span v-if="log.inventoryChange >= 0" class="text-success font-weight-bold">
                      + {{ formatNumber(log.inventoryChange) }} <span class="dfp-unit">L</span>
                    </span>
                  </td>
                  <td>
                    <span v-if="log.inventoryChange < 0" class="text-danger font-weight-bold">
                      - {{ formatNumber(Math.abs(log.inventoryChange)) }} <span class="dfp-unit">L</span>
                    </span>
                  </td>
                  <td>{{ formatNumber(log.endInventory) }} <span class="dfp-unit">L</span></td>
                  <td class="text-center">
                    <template v-if="log.entryType === 'fuelcons' && log.debug">
                      <i v-if="hasCoverageIssues(log.debug)" class="ri-error-warning-line text-warning" style="font-size:1rem;"></i>
                      <i v-else class="ri-checkbox-circle-line text-success" style="font-size:1rem;"></i>
                    </template>
                  </td>
                </tr>
                <tr v-if="log.debug" :key="'debug-' + index">
                  <td colspan="7" class="p-0">
                    <div class="dfp">
                      <template v-if="isDebugRowExpanded(index)">
                      <!-- A: Ranges + Devices -->
                      <div class="dfp-top">
                        <div class="dfp-box">
                          <div class="dfp-box-title">Ranges</div>
                          <table class="dfp-range-table">
                            <tbody>
                              <tr>
                                <td class="dfp-range-label">Requested</td>
                                <td class="dfp-range-val">{{ formatUtcDateToLocal(log.debug.requestedRange.startDate) }}</td>
                                <td class="dfp-range-arrow">→</td>
                                <td class="dfp-range-val">{{ formatUtcDateToLocal(log.debug.requestedRange.endDate) }}</td>
                              </tr>
                              <tr>
                                <td class="dfp-range-label">Flowmeter</td>
                                <td class="dfp-range-val">{{ formatUtcDateToLocal(log.debug.flowmeterRange.startDate) }}</td>
                                <td class="dfp-range-arrow">→</td>
                                <td class="dfp-range-val">{{ formatUtcDateToLocal(log.debug.flowmeterRange.endDate) }}</td>
                              </tr>
                              <tr>
                                <td class="dfp-range-label">AE</td>
                                <td class="dfp-range-val">{{ formatUtcDateToLocal(log.debug.aeRange.startDate) }}</td>
                                <td class="dfp-range-arrow">→</td>
                                <td class="dfp-range-val">{{ formatUtcDateToLocal(log.debug.aeRange.endDate) }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div class="dfp-box">
                          <div class="dfp-box-title">Devices</div>
                          <div class="dfp-devices">
                            <span class="dfp-dev-label">FM</span>
                            <span v-for="d in log.debug.devices.calculatedFlowmeter" :key="d.id" class="badge badge-info mr-1">{{ d.label }}</span>
                            <span class="dfp-sep">·</span>
                            <span class="dfp-dev-label">AE</span>
                            <span v-for="d in log.debug.devices.calculatedAe" :key="d.id" class="badge badge-secondary mr-1">{{ d.label }}</span>
                            <template v-if="log.debug.devices.excluded.length">
                              <span class="dfp-sep">·</span>
                              <span class="dfp-dev-label">Excl</span>
                              <span v-for="d in log.debug.devices.excluded" :key="d.id" class="badge badge-light border text-muted mr-1">{{ d.label }}</span>
                            </template>
                          </div>
                        </div>
                        <div v-if="rollupBadges(log.debug).length" class="dfp-box">
                          <div class="dfp-box-title">Rollup</div>
                          <div class="dfp-devices">
                            <span v-for="b in rollupBadges(log.debug)" :key="b.type" class="badge mr-1"
                              :class="b.warn ? 'badge-warning' : 'badge-light border text-muted'">{{ b.type }} · {{ b.label }}</span>
                          </div>
                        </div>
                      </div>
                      <!-- B: FM Inventory -->
                      <div class="dfp-box">
                        <div class="dfp-box-title">Flowmeter Inventory</div>
                        <table class="dfp-table">
                          <thead>
                            <tr>
                              <th>Device</th>
                              <th>First Inventory (L)</th>
                              <th>Last Inventory (L)</th>
                              <th>Volume (L)</th>
                              <th>Window</th>
                              <th>Coverage</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(inv, deviceId) in log.debug.flowmeterInventory" :key="deviceId"
                              :class="{ 'dfp-row-warn': inv.coverage.missingStart || inv.coverage.missingEnd || inv.backward }">
                              <td class="dfp-mono">{{ deviceId }}</td>
                              <td>{{ $fmt(inv.first_volInvent, 3) }} <span class="dfp-unit">L</span></td>
                              <td>{{ $fmt(inv.last_volInvent, 3) }} <span class="dfp-unit">L</span></td>
                              <td>
                                {{ $fmt(inv.volume, 3) }} <span class="dfp-unit">L</span>
                                <div v-if="inv.backward" class="text-danger dfp-time-small">
                                  Δ {{ $fmt(inv.delta, 3) }} L · meter reset?
                                </div>
                              </td>
                              <td class="dfp-mono dfp-time-small">{{ formatUtcDateToLocal(inv.firstTimestamp) }} → {{ formatUtcDateToLocal(inv.lastTimestamp) }}</td>
                              <td>
                                <div v-if="!inv.coverage.missingStart && !inv.coverage.missingEnd" class="dfp-ok">✓ OK</div>
                                <div v-else class="dfp-warn">
                                  <i class="ri-error-warning-line"></i>
                                  <span v-if="inv.coverage.missingStart"> Miss Start</span>
                                  <span v-if="inv.coverage.missingEnd"> Miss End</span>
                                </div>
                                <div class="dfp-gap-vals">
                                  <span class="dfp-gap-label">start</span>
                                  <span :class="inv.coverage.missingStart ? 'text-danger' : inv.coverage.startGapSeconds > 0 ? 'text-warning' : 'text-muted'">{{ formatGapDuration(inv.coverage.startGapSeconds) }}</span>
                                  <span class="text-muted mx-1">·</span>
                                  <span class="dfp-gap-label">end</span>
                                  <span :class="inv.coverage.missingEnd ? 'text-danger' : inv.coverage.endGapSeconds > 0 ? 'text-warning' : 'text-muted'">{{ formatGapDuration(inv.coverage.endGapSeconds) }}</span>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <!-- C: Consumption -->
                      <div class="dfp-box">
                        <div class="dfp-box-title">Consumption</div>
                        <table class="dfp-table">
                          <thead>
                            <tr>
                              <th>Source</th>
                              <th>Breakdown</th>
                              <th>Subtotal (L)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><span class="badge badge-info">Flowmeter</span></td>
                              <td>
                                <span v-for="(val, side) in log.debug.flowmeterCons" :key="side" class="mr-3">
                                  <span class="dfp-cons-sublabel">{{ side }}</span> {{ formatNumber(val) }} <span class="dfp-unit">L</span>
                                </span>
                              </td>
                              <td><strong>{{ formatNumber(log.debug.flowmeterTotal) }}</strong> <span class="dfp-unit">L</span></td>
                            </tr>
                            <template v-for="(cons, aeId) in log.debug.aeCons">
                              <tr v-if="aeId !== 'total'" :key="aeId">
                                <td>
                                  <span class="badge badge-secondary">AE</span>
                                  <small class="dfp-mono ml-1">{{ aeId }}</small>
                                </td>
                                <td>
                                  <span v-if="log.debug.aeRunningSeconds && log.debug.aeRunningSeconds[aeId]" class="mr-3">
                                    <span class="dfp-cons-sublabel">Run</span>
                                    {{ log.debug.aeRunningSeconds[aeId].map(v => formatSecondsToHms(v)).join(' + ') }}
                                  </span>
                                  <span class="dfp-cons-sublabel">Cons</span>
                                  {{ cons.map(v => formatNumber(v)).join(' + ') }} <span class="dfp-unit">L</span>
                                </td>
                                <td><strong>{{ formatNumber(cons.reduce((a, b) => a + b, 0)) }}</strong> <span class="dfp-unit">L</span></td>
                              </tr>
                            </template>
                            <tr class="dfp-total-row">
                              <td colspan="2" class="text-right">Grand Total</td>
                              <td><strong class="dfp-grand-total">{{ formatNumber(log.debug.total) }}</strong> <span class="dfp-unit">L</span></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      </template>
                    </div>
                  </td>
                </tr>
              </template>
              <tr v-if="flattenedLogs.length === 0">
                <td colspan="7" class="text-center py-4 text-muted">
                  <i class="ri-file-list-3-line d-block mb-1" style="font-size: 2rem; color: #dee2e6;"></i>
                  No log data available for this interval.
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>

        <!-- Mobile: card log -->
        <div class="log-mobile-cards">
          <div v-if="flattenedLogs.length === 0" class="text-center py-4 text-muted">
            <i class="ri-file-list-3-line d-block mb-1" style="font-size: 2rem; color: #dee2e6;"></i>
            No log data available for this interval.
          </div>
          <div v-else class="log-card-list">
            <div v-for="(log, index) in flattenedLogs" :key="'card-' + index" class="log-card">
              <div class="log-card-header"
                :class="{ 'log-card-header--clickable': log.entryType === 'fuelcons' && log.debug }"
                @click="log.entryType === 'fuelcons' && log.debug ? toggleDebugRow(index) : null">
                <div class="d-flex align-items-center" style="gap: 8px;">
                  <span class="badge" :class="entryTypeBadgeClass(log.entryType)">{{ formatEntryType(log.entryType) }}</span>
                  <template v-if="log.entryType === 'fuelcons' && log.debug">
                    <i v-if="hasCoverageIssues(log.debug)" class="ri-error-warning-line text-warning"></i>
                    <i v-else class="ri-checkbox-circle-line text-success"></i>
                  </template>
                </div>
                <span class="log-card-time">{{ formatUnixTimestampToLocal(log.timestamp) }}</span>
              </div>
              <div class="log-card-body">
                <div class="log-card-row" v-if="log.note">
                  <span class="log-card-label">Note</span>
                  <a href="#" class="log-card-value" @click.prevent="$emit('show-note', log.note)">
                    {{ log.note.length > 40 ? log.note.substring(0, 40) + '...' : log.note }}
                  </a>
                </div>
                <div class="log-card-row" v-if="log.inventoryChange >= 0">
                  <span class="log-card-label">Addition</span>
                  <span class="log-card-value text-success font-weight-bold">+ {{ formatNumber(log.inventoryChange) }} L</span>
                </div>
                <div class="log-card-row" v-if="log.inventoryChange < 0">
                  <span class="log-card-label">Reduction</span>
                  <span class="log-card-value text-danger font-weight-bold">- {{ formatNumber(Math.abs(log.inventoryChange)) }} L</span>
                </div>
                <div class="log-card-row">
                  <span class="log-card-label">ROB Balance</span>
                  <span class="log-card-value">{{ formatNumber(log.endInventory) }} L</span>
                </div>
              </div>
              <!-- Debug panel jika ada -->
              <div v-if="log.debug && isDebugRowExpanded(index)" class="dfp">
                <template>
                  <div class="dfp-top">
                    <div class="dfp-box">
                      <div class="dfp-box-title">Ranges</div>
                      <table class="dfp-range-table">
                        <tbody>
                          <tr><td class="dfp-range-label">Requested</td><td class="dfp-range-val">{{ formatUtcDateToLocal(log.debug.requestedRange.startDate) }}</td><td class="dfp-range-arrow">→</td><td class="dfp-range-val">{{ formatUtcDateToLocal(log.debug.requestedRange.endDate) }}</td></tr>
                          <tr><td class="dfp-range-label">Flowmeter</td><td class="dfp-range-val">{{ formatUtcDateToLocal(log.debug.flowmeterRange.startDate) }}</td><td class="dfp-range-arrow">→</td><td class="dfp-range-val">{{ formatUtcDateToLocal(log.debug.flowmeterRange.endDate) }}</td></tr>
                          <tr><td class="dfp-range-label">AE</td><td class="dfp-range-val">{{ formatUtcDateToLocal(log.debug.aeRange.startDate) }}</td><td class="dfp-range-arrow">→</td><td class="dfp-range-val">{{ formatUtcDateToLocal(log.debug.aeRange.endDate) }}</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="dfp-box">
                      <div class="dfp-box-title">Devices</div>
                      <div class="dfp-devices">
                        <span class="dfp-dev-label">FM</span>
                        <span v-for="d in log.debug.devices.calculatedFlowmeter" :key="d.id" class="badge badge-info mr-1">{{ d.label }}</span>
                        <span class="dfp-sep">·</span>
                        <span class="dfp-dev-label">AE</span>
                        <span v-for="d in log.debug.devices.calculatedAe" :key="d.id" class="badge badge-secondary mr-1">{{ d.label }}</span>
                      </div>
                    </div>
                    <div v-if="rollupBadges(log.debug).length" class="dfp-box">
                      <div class="dfp-box-title">Rollup</div>
                      <div class="dfp-devices">
                        <span v-for="b in rollupBadges(log.debug)" :key="b.type" class="badge mr-1"
                          :class="b.warn ? 'badge-warning' : 'badge-light border text-muted'">{{ b.type }} · {{ b.label }}</span>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
        </template>
      </div>

      <!-- Adjustment Table -->
      <div v-if="detailViewType === 'adjustment' && canRead('master_rob_adjustment')">
        <h5>Adjustments</h5>
        <div v-if="adjustmentLoading" class="text-center py-4">
          <b-spinner variant="primary" small></b-spinner>
          <span class="ml-2 text-muted">Loading adjustments...</span>
        </div>
        <div v-else-if="adjustmentError" class="alert alert-danger d-flex align-items-center">
          <i class="ri-error-warning-line mr-2" style="font-size: 1.1rem;"></i>
          {{ adjustmentError }}
        </div>
        <div v-else-if="Array.isArray(adjustmentData) && adjustmentData.length > 0">
          <!-- Desktop: tabel adjustment -->
          <div class="adj-desktop-table table-responsive">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Value</th>
                  <th>Note</th>
                  <th>Created By</th>
                  <th>Created At</th>
                  <th v-if="canManageRobAdjustment">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="adj in adjustmentData" :key="adj.robaId">
                  <td>{{ formatDateToLocal(adj.robaTime) }}</td>
                  <td>{{ formatAdjustmentType(adj.robaType) }}</td>
                  <td>{{ formatNumber(adj.robaValue) }}</td>
                  <td>
                    <a href="#" v-if="adj.robaNote" @click.prevent="$emit('show-note', adj.robaNote)">
                      {{ adj.robaNote.length > 20 ? adj.robaNote.substring(0, 20) + '...' : adj.robaNote }}
                    </a>
                  </td>
                  <td>{{ adj.robaCreatedUserId }}</td>
                  <td>{{ formatDateToLocal(adj.robaCreatedTime) }}</td>
                  <td v-if="canManageRobAdjustment">
                    <button v-if="canUpdate('master_rob_adjustment')" class="btn btn-warning btn-sm mr-1"
                      @click="$emit('edit-adjustment', adj)">Edit</button>
                    <button v-if="canDelete('master_rob_adjustment')" class="btn btn-sm"
                      :class="confirmingDeleteAdjustmentId === adj.robaId ? 'btn-danger' : 'btn-outline-danger'"
                      @click="$emit('delete-adjustment', adj)" @mouseleave="$emit('update:confirmingDeleteAdjustmentId', null)">
                      {{ confirmingDeleteAdjustmentId === adj.robaId ? 'Confirm?' : 'Delete' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile: card adjustment -->
          <div class="adj-mobile-cards">
            <div class="rob-adj-card-list">
              <div v-for="adj in adjustmentData" :key="adj.robaId" class="rob-adj-card">
                <div class="rob-adj-card-header">
                  <span class="rob-adj-card-date">{{ formatDateToLocal(adj.robaTime) }}</span>
                  <span class="badge" :class="adj.robaType == 1 ? 'badge-warning' : 'badge-info'">
                    {{ formatAdjustmentType(adj.robaType) }}
                  </span>
                </div>
                <div class="rob-adj-card-body">
                  <div class="rob-adj-card-row">
                    <span class="rob-adj-card-label">Value</span>
                    <span class="rob-adj-card-value">{{ formatNumber(adj.robaValue) }}</span>
                  </div>
                  <div class="rob-adj-card-row" v-if="adj.robaNote">
                    <span class="rob-adj-card-label">Note</span>
                    <a href="#" class="rob-adj-card-value" @click.prevent="$emit('show-note', adj.robaNote)">
                      {{ adj.robaNote.length > 30 ? adj.robaNote.substring(0, 30) + '...' : adj.robaNote }}
                    </a>
                  </div>
                  <div class="rob-adj-card-row">
                    <span class="rob-adj-card-label">Created By</span>
                    <span class="rob-adj-card-value">{{ adj.robaCreatedUserId || '-' }}</span>
                  </div>
                  <div class="rob-adj-card-row">
                    <span class="rob-adj-card-label">Created At</span>
                    <span class="rob-adj-card-value">{{ formatDateToLocal(adj.robaCreatedTime) }}</span>
                  </div>
                </div>
                <div class="rob-adj-card-footer" v-if="canManageRobAdjustment">
                  <button v-if="canUpdate('master_rob_adjustment')" class="btn btn-warning btn-sm"
                    @click="$emit('edit-adjustment', adj)">Edit</button>
                  <button v-if="canDelete('master_rob_adjustment')" class="btn btn-sm"
                    :class="confirmingDeleteAdjustmentId === adj.robaId ? 'btn-danger' : 'btn-outline-danger'"
                    @click="$emit('delete-adjustment', adj)" @mouseleave="$emit('update:confirmingDeleteAdjustmentId', null)">
                    {{ confirmingDeleteAdjustmentId === adj.robaId ? 'Confirm?' : 'Delete' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-4">
          <i class="ri-equalizer-line" style="font-size: 3rem; color: #dee2e6;"></i>
          <p class="text-muted mt-2 mb-0">No adjustments recorded for this profile.</p>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-5">
      <i class="ri-information-line" style="font-size: 3rem; color: #dee2e6;"></i>
      <p class="text-muted mt-2 mb-0">No detail data available.</p>
    </div>
  </div>
</template>

<style scoped>
/* Log table show/hide */
.log-desktop-table { display: block; }
.log-mobile-cards  { display: none; }

/* Adjustment table show/hide */
.adj-desktop-table { display: block; }
.adj-mobile-cards  { display: none; }

@media (max-width: 767px) {
  .log-desktop-table { display: none; }
  .log-mobile-cards  { display: block; }
  .adj-desktop-table { display: none; }
  .adj-mobile-cards  { display: block; }
}

/* Log card */
.log-card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0 12px;
}

.log-card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.log-card-header {
  padding: 8px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.log-card-header--clickable { cursor: pointer; }
.log-card-header--clickable:active { background: #e9ecef; }

.log-card-time {
  font-size: 12px;
  color: #6c757d;
}

.log-card-body { padding: 8px 14px; }

.log-card-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
  gap: 8px;
}

.log-card-row:last-child { border-bottom: none; }

.log-card-label {
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
  flex-shrink: 0;
  min-width: 80px;
}

.log-card-value {
  font-size: 13px;
  color: #212529;
  text-align: right;
}

/* Adjustment card */
.rob-adj-card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0 12px;
}

.rob-adj-card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.rob-adj-card-header {
  padding: 8px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.rob-adj-card-date {
  font-size: 13px;
  font-weight: 600;
  color: #343a40;
}

.rob-adj-card-body { padding: 8px 14px; }

.rob-adj-card-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
  gap: 8px;
}

.rob-adj-card-row:last-child { border-bottom: none; }

.rob-adj-card-label {
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
  flex-shrink: 0;
  min-width: 90px;
}

.rob-adj-card-value {
  font-size: 13px;
  color: #212529;
  text-align: right;
}

.rob-adj-card-footer {
  padding: 8px 14px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

.table-responsive .table th,
.table-responsive .table td {
  white-space: nowrap;
}

.summary-card {
  border: 1px solid #e8ecf3;
  border-radius: .25rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease-in-out;
}

.summary-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.summary-card .card-header {
  border-bottom: none;
  padding: 0.75rem 1rem;
}

.summary-card .card-body {
  padding: 1.5rem 1rem;
}

/* Debug full panel */
.dfp {
  background: #f6fbff;
  border-left: 3px solid #17a2b8;
  display: flex;
  flex-direction: column;
  gap: 0;
  font-size: 0.9rem;
}

.dfp-top {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  border-bottom: 1px solid #d0eaf5;
}

.dfp-top > .dfp-box:first-child {
  border-right: 1px solid #d0eaf5;
  flex: 0 0 auto;
}

.dfp-top > .dfp-box:last-child {
  flex: 1 1 auto;
}

.dfp-box {
  padding: 10px 14px;
  border-bottom: 1px solid #d0eaf5;
}

.dfp-box:last-child {
  border-bottom: none;
}

.dfp-box-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #17a2b8;
  margin-bottom: 8px;
}

.dfp-range-table {
  border-collapse: collapse;
  font-size: 0.9rem;
}

.dfp-range-table td {
  padding: 2px 8px 2px 0;
  white-space: nowrap;
}

.dfp-range-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #6c757d;
  width: 80px;
}

.dfp-range-val {
  font-family: 'Roboto Mono', monospace;
  color: #343a40;
}

.dfp-range-arrow {
  color: #adb5bd;
  padding: 0 6px;
}

.dfp-devices {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  font-size: 0.9rem;
}

.dfp-dev-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #6c757d;
  letter-spacing: 0.04em;
}

.dfp-sep {
  color: #ced4da;
  margin: 0 2px;
}

.dfp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.dfp-table th {
  background: #e8f4f8;
  color: #495057;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 5px 10px;
  border-bottom: 1px solid #c5dce8;
  white-space: nowrap;
}

.dfp-table td {
  padding: 5px 10px;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

.dfp-table tbody tr:last-child td {
  border-bottom: none;
}

.dfp-row-warn {
  background: #fff8e1;
}

.dfp-mono {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.88rem;
}

.dfp-time-small {
  font-size: 0.85rem;
  color: #6c757d;
}

.dfp-ok {
  color: #28a745;
  font-size: 0.88rem;
}

.dfp-warn {
  color: #e67e22;
  font-size: 0.88rem;
}

.dfp-gap-vals {
  font-size: 0.78rem;
  margin-top: 2px;
}

.dfp-gap-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  font-weight: 700;
  color: #adb5bd;
  margin-right: 2px;
}

.dfp-cons-sublabel {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6c757d;
  font-weight: 700;
  margin-right: 3px;
}

.dfp-unit {
  font-size: 0.72rem;
  color: #adb5bd;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.dfp-total-row td {
  background: #e8f4f8;
  font-size: 0.9rem;
  border-top: 1px solid #c5dce8 !important;
}

.dfp-grand-total {
  font-size: 1.05rem;
  color: #007bff;
}

.log-row-stripe {
  background-color: rgba(0, 0, 0, 0.05);
}

.dfp-clickable-row {
  cursor: pointer;
}

.dfp-clickable-row:hover td {
  background: #edf6fb !important;
}

.dfp-row-active td {
  background: #daeef7 !important;
}

</style>
